import { useRef, useEffect, useCallback } from 'react';

/**
 * useExamWebSocket v4 — Maximum speed camera streaming
 *
 * Key optimizations:
 * 1. setTimeout(300ms) instead of setInterval(500ms) — adaptive timing
 * 2. Persistent OffscreenCanvas (or regular canvas) — zero allocation per frame
 * 3. 240×180 JPEG at quality 0.5 → ~4-8KB/frame, encodes in ~2-4ms
 * 4. Frame sent as JSON with base64 (WS binary would need server changes)
 * 5. Skip frame if WS buffer is backing up (prevents lag accumulation)
 * 6. Violation snapshot: 640×480 JPEG at quality 0.85 for clear evidence
 */
export function useExamWebSocket({ attemptId, examId, enabled = false }) {
  const wsRef       = useRef(null);
  const reconnRef   = useRef(null);
  const mountedRef  = useRef(true);
  const timerRef    = useRef(null);
  const skippedRef  = useRef(0); // track skipped frames for adaptive FPS

  // Persistent canvases — allocated once ever
  const streamCanvas   = useRef(null);
  const snapshotCanvas = useRef(null);
  const videoElRef     = useRef(null);

  // Callbacks
  const onActionRef    = useRef(null);
  const onMessageRef   = useRef(null);
  const onCameraReqRef = useRef(null);

  const getStreamCanvas = () => {
    if (!streamCanvas.current) {
      const c = document.createElement('canvas');
      c.width = 240; c.height = 180;
      streamCanvas.current = c;
    }
    return streamCanvas.current;
  };

  const getSnapshotCanvas = () => {
    if (!snapshotCanvas.current) {
      const c = document.createElement('canvas');
      c.width = 640; c.height = 480;
      snapshotCanvas.current = c;
    }
    return snapshotCanvas.current;
  };

  // ── Push one streaming frame ──────────────────────────────────────────────
  const pushFrame = useCallback(() => {
    const video = videoElRef.current;
    const ws    = wsRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0) return;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    // Skip if WS send buffer is backing up (prevents lag snowball)
    if (ws.bufferedAmount > 32000) {
      skippedRef.current++;
      return;
    }
    skippedRef.current = 0;

    try {
      const canvas = getStreamCanvas();
      const ctx = canvas.getContext('2d', { willReadFrequently: false });
      ctx.drawImage(video, 0, 0, 240, 180);
      // JPEG 0.5 quality at 240×180 = ~4-8KB — fast to encode
      ws.send(JSON.stringify({
        type:  'camera_frame',
        frame: canvas.toDataURL('image/jpeg', 0.5),
      }));
    } catch { /* never crash exam */ }
  }, []);

  // ── Adaptive streaming loop ───────────────────────────────────────────────
  // Uses setTimeout (not setInterval) so frame N+1 starts AFTER frame N finishes
  // This prevents frame queuing and lag buildup
  const scheduleNext = useCallback(() => {
    if (!mountedRef.current) return;
    // Adaptive: if we had to skip frames, slow down slightly to let buffer drain
    const delay = skippedRef.current > 0 ? 600 : 300; // ~3fps or ~1.6fps
    timerRef.current = setTimeout(() => {
      pushFrame();
      scheduleNext();
    }, delay);
  }, [pushFrame]);

  const startStream = useCallback(() => {
    if (timerRef.current) return;
    scheduleNext();
  }, [scheduleNext]);

  const stopStream = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  // ── Violation screenshot (high-res, for evidence) ─────────────────────────
  const captureViolationSnapshot = useCallback(() => {
    const video = videoElRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0) return null;
    try {
      const canvas = getSnapshotCanvas();
      canvas.getContext('2d').drawImage(video, 0, 0, 640, 480);
      return canvas.toDataURL('image/jpeg', 0.85); // high quality for evidence
    } catch { return null; }
  }, []);

  // ── Connect ───────────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (!enabled || !attemptId || !examId) return;
    const token = sessionStorage.getItem('token');
    if (!token) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host  = window.location.hostname;
    const port  = process.env.NODE_ENV === 'development' ? '5000' : window.location.port;
    const url   = `${proto}://${host}:${port}/ws?token=${token}&role=student&examId=${examId}&attemptId=${attemptId}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Student connected — camera stream at ~3fps');
        startStream();
      };
      ws.onclose = () => {
        stopStream();
        if (!mountedRef.current) return;
        reconnRef.current = setTimeout(connect, 5000);
      };
      ws.onerror = () => {};
      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === 'instructor_action')  onActionRef.current?.(msg);
        if (msg.type === 'instructor_message') onMessageRef.current?.(msg);
        if (msg.type === 'send_camera_frame')  { pushFrame(); onCameraReqRef.current?.(); }
      };
    } catch (err) {
      console.warn('[WS] Connect failed:', err.message);
    }
  }, [enabled, attemptId, examId, startStream, stopStream, pushFrame]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      stopStream();
      clearTimeout(reconnRef.current);
      wsRef.current?.close();
    };
  }, [connect, stopStream]);

  const setOnAction    = useCallback((fn) => { onActionRef.current    = fn; }, []);
  const setOnMessage   = useCallback((fn) => { onMessageRef.current   = fn; }, []);
  const setOnCameraReq = useCallback((fn) => { onCameraReqRef.current = fn; }, []);
  const setVideoEl     = useCallback((el) => { videoElRef.current     = el; }, []);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    try { wsRef.current.send(JSON.stringify(data)); } catch { }
  }, []);

  const sendHeartbeat   = useCallback((d)     => send({ type: 'heartbeat',       ...d }), [send]);
  const sendActivity    = useCallback((d)     => send({ type: 'question_change', ...d }), [send]);
  const sendSubmit      = useCallback((d)     => send({ type: 'submit',          ...d }), [send]);
  const sendCameraFrame = useCallback((frame) => send({ type: 'camera_frame',   frame }), [send]);

  return {
    wsRef,
    sendHeartbeat, sendActivity, sendSubmit, sendCameraFrame,
    setOnAction, setOnMessage, setOnCameraReq,
    setVideoEl,
    captureViolationSnapshot,
  };
}
